import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { AlertController } from 'providers/alert-controller';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { getMyApp } from 'app/app-interface';
import * as Sentry from '@sentry/angular';
import { LoggingService, errorReportFields } from 'services/logging.service';
import { SettingsService } from 'services/settings.service';
import { TelemetryService } from 'services/telemetry.service';
import { LoadingController } from 'providers/loading-controller';
import { SendFeedbackType } from '@cf-shared/send-feedback-interface';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { IHomePage } from 'pages/home/home-interface';
import { ISendFeedbackPage, SendFeedbackPageParams } from './send-feedback-interface';
import { UserDb } from 'services/user-db.service';
import { TranslatedString } from 'models/translated-string';
import { CsScreenshotAnnotatorComponent } from 'components/cs-screenshot-annotator/cs-screenshot-annotator';

enum FeedbackState {
  ChooseType,
  WithScreenshot,
  WithOutScreenshot,
}

interface FeedbackTypeInfo {
  type: SendFeedbackType;
  title: string;
  description: string;
  placeholder: TranslatedString;
  icon: string;
};

@Component({
  selector: 'page-send-feedback',
  templateUrl: 'send-feedback.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendFeedbackPage extends CsPage<SendFeedbackPageParams> {
  protected LL = LL;

  @ViewChild('textarea', { static: false }) textarea: ElementRef;
  @ViewChild(CsScreenshotAnnotatorComponent, { static: false }) annotator: CsScreenshotAnnotatorComponent;

  /** the captured screenshot to annotate, taken off the round trip on the way in */
  screenshotDataUrl: string | undefined;

  FeedbackState = FeedbackState;
  SendFeedbackType = SendFeedbackType;
  state: FeedbackState = FeedbackState.ChooseType;
  type: SendFeedbackType = SendFeedbackType.Unknown;
  feedbackDescription: string = '';
  receiveEmailNotification: boolean = false;

  typeInfos: FeedbackTypeInfo[] = [
    {
      type: SendFeedbackType.Question,
      title: LL.sendFeedback.types.question.title(),
      description: LL.sendFeedback.types.question.description(),
      placeholder: LL.sendFeedback.types.question.placeholder(),
      icon: 'help-circle'
    },
    {
      type: SendFeedbackType.Like,
      title: LL.sendFeedback.types.like.title(),
      description: LL.sendFeedback.types.like.description(),
      placeholder: LL.sendFeedback.types.like.placeholder(),
      icon: 'thumbs-up'
    },
    {
      type: SendFeedbackType.Dislike,
      title: LL.sendFeedback.types.dislike.title(),
      description: LL.sendFeedback.types.dislike.description(),
      placeholder: LL.sendFeedback.types.dislike.placeholder(),
      icon: 'thumbs-down'
    },
    {
      type: SendFeedbackType.Issue,
      title: LL.sendFeedback.types.issue.title(),
      description: LL.sendFeedback.types.issue.description(),
      placeholder: LL.sendFeedback.types.issue.placeholder(),
      icon: 'bug'
    },
    {
      type: SendFeedbackType.Suggestion,
      title: LL.sendFeedback.types.suggestion.title(),
      description: LL.sendFeedback.types.suggestion.description(),
      placeholder: LL.sendFeedback.types.suggestion.placeholder(),
      icon: 'bulb'
    }
  ];

  title: string;
  placeholder: TranslatedString;

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private cloudFunctionsService: CloudFunctionsService,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private logging: LoggingService,
    private telemetry: TelemetryService,
    private cdRef: ChangeDetectorRef,
    private userDb: UserDb,
    private settings: SettingsService,
    private alertCtrl: AlertController,
  ) {
    super();
  }

  async ionViewWillEnter() {
    const type = this.navParams.data.type;
    const hasScreenshot = getMyApp().screenshotTrip.hasScreenshot;
    // `onAddScreenshotClicked` always stashes the typed text before it leaves — an
    // empty string when nothing was typed — and the trip only hands that text back to
    // the page that stashed it. So getting text back means the user is coming back from
    // that trip, whether or not the capture produced an image. Taking it also clears it,
    // so read it once here and use the value below
    const stash = getMyApp().screenshotTrip.takeStashFor(ISendFeedbackPage);
    const stashedDescription = stash?.text;
    // the page that comes back from that trip was pushed by the camera button, which
    // passes no error id, so the id this report is about comes back from the stash. an
    // id passed in the parameters is newer than the stash, so it wins
    this.errorIdFromCaller = this.navParams.data.sentryId || stash?.errorId;
    if (type !== undefined && (hasScreenshot || stashedDescription !== undefined)) {
      this.type = type;
      this.updateTypeInfo();
      // with no image there is nothing to draw on, so put the user back on the
      // plain form rather than on the screenshot editor
      this.state = hasScreenshot ? FeedbackState.WithScreenshot : FeedbackState.WithOutScreenshot;
    }
    // take the captured image only once this page has committed to showing the
    // editor, and hand it to the annotator as an input. taking it also clears it,
    // so taking it any earlier would throw away a capture the page never displays:
    // the app pushes this page with no feedback type when the user reaches it from
    // the "noticed something unusual?" toast, and that lands on the type picker
    if (hasScreenshot && this.state === FeedbackState.WithScreenshot) {
      this.screenshotDataUrl = getMyApp().screenshotTrip.takeScreenshot();
    }
    // restore text typed before the user left to take a screenshot. this must not
    // depend on the capture having worked: a capture that failed comes back here
    // with no image, and only reading the stash when an image existed threw away
    // everything the user had written, with nothing telling them so
    if (stashedDescription !== undefined) {
      this.feedbackDescription = stashedDescription;
    }
    this.cdRef.markForCheck();
  }

  async ngAfterViewInit() {
  }

  async ionViewCanEnter() {
  }

  async ionViewDidEnter() {
    // the annotator is only on screen in the screenshot state, and it is started
    // from here rather than from its own lifecycle hook because it sizes the
    // canvas by measuring where this page's form ended up
    this.annotator?.initAfterEnter();

    // hide the screenshot button always, even if we didn't show it
    // that allows the user to disable it by re-entering the page
    getMyApp().hideScreenshotButton();
    this.cdRef.markForCheck();
  }

  onTypeSelected(type: SendFeedbackType) {
    this.type = type;
    this.state = FeedbackState.WithOutScreenshot;
    this.updateTypeInfo();
    this.cdRef.markForCheck();
  }

  private updateTypeInfo() {
    let info = this.typeInfos.find(i => i.type === this.type);
    if (info) {
      this.title = info.title;
      this.placeholder = info.placeholder;
    }
    this.cdRef.markForCheck();
  }

  onAddScreenshotClicked() {
    // this page keeps its text but does not ask to be returned to: the app's own
    // fallback pushes this page WITH its parameters, which a return push would lack
    getMyApp().screenshotTrip.stashText(ISendFeedbackPage, this.feedbackDescription, this.errorIdFromCaller);
    getMyApp().showScreenshotButton(this.type);
    // the text is stashed above and restored on the way back in, so this trip
    // loses nothing and must not raise the discard question
    this.leaveWithoutAsking.set();
    void this.exitFeedbackPage();
  }

  private isExiting = false;

  async onSubmitButtonClicked() {
    if (this.isExiting) return;
    this.isExiting = true;
    await this.recordUnhappyFeedbackForReviewPromptAsync();
    let reportSent = await this.submitfeedbackReport();
    // if the report did not go, stay on the page so the text is still here to
    // try again with. Closing anyway threw it away at the exact moment the user
    // was told to try again, which left them nothing to try again WITH.
    if (!reportSent) {
      // release the re-entry guard, or both Send and Close stay dead for the
      // rest of the page's life and the user is trapped on it.
      this.isExiting = false;
      return;
    }
    // The report has gone. The box still holds its text — nothing clears it —
    // so without this the guard would ask "Discard your feedback?" about
    // feedback that was already sent, and answering "Keep Editing" would strand
    // the user on a page whose Send button is now dead.
    this.leaveWithoutAsking.set();
    await this.exitFeedbackPage();
  }

  /**
   * #4625 — remember locally that this person told us something was wrong, so we
   * stay quiet about rating the app for a while.
   *
   * **Only the two unhappy kinds count.** A question or a suggestion is not a
   * complaint, and Like is the opposite of one — treating all five as unhappiness
   * would silence the prompt for anybody who ever wrote to us at all.
   *
   * Recorded BEFORE the report is sent, and deliberately not conditional on it
   * succeeding. What matters is that they were unhappy enough to write, which is
   * already true by this point; a send that fails on a flaky connection does not
   * make them happy again. The telemetry these kinds already emit is no use here,
   * because the app cannot read its own telemetry back.
   *
   * A failure to save is reported and swallowed rather than allowed to block the
   * submission — losing this marker means at worst one prompt shown at an
   * unwelcome moment, while a feedback report that could not be filed because a
   * local write failed would be far worse.
   */
  private async recordUnhappyFeedbackForReviewPromptAsync(): Promise<void> {
    if (this.type !== SendFeedbackType.Issue && this.type !== SendFeedbackType.Dislike) return;
    try {
      await this.settings.recordProblemReportedAsync();
    } catch (err) {
      this.logging.trackSilentProblem('ReviewPromptProblemMarkerSaveFailed',
        errorReportFields(err));
    }
  }

  async onCloseButtonClicked() {
    if (this.isExiting) return;
    // The close button must ask for ITSELF, because this page does not always
    // leave by popping: with nothing to go back to it sets a new root page
    // instead, and `cs-nav` deliberately does not run the leave guard for that.
    // So the guard alone would protect some exits and not others.
    if (!await this.confirmDiscardIfWrittenAsync()) return;
    this.isExiting = true;
    this.leaveWithoutAsking.set();
    this.telemetry.trackEvent('feedback_canceled', {});
    await this.exitFeedbackPage();
  }

  // custom handling, because sometimes we can go back to the previous page, sometimes not
  async exitFeedbackPage() {
    if (this.navCtrl.canGoBack()) {
      await this.navCtrl.popAsync();
    } else {
      await this.navCtrl.setRootAsync(IHomePage, {});
    }
  }

  /**
   * Runs before the page closes on a pop — the browser back button, Android's
   * hardware back button or a swipe back. Without it, feedback the user had
   * typed, and any drawing they had made on the screenshot, was thrown away in
   * silence (#5058).
   *
   * There is no saving a half-written report, so the only real question is
   * whether they meant to discard it.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // the paths that have already settled what happens to the feedback arm this,
    // so the guard does not ask a second time. Each has its own reason:
    //
    // - sending it, and the send SUCCEEDING. The report has gone; the box still
    //   holds its text. A send that failed deliberately does not arm it, because
    //   that page stays open — see `onSubmitButtonClicked`.
    // - the close button, which asks the discard question ITSELF (see
    //   `onCloseButtonClicked` for why it cannot rely on the guard).
    // - the hop out to take a screenshot, which deliberately leaves this page and
    //   comes back to it, having stashed the typed text first — so asking there
    //   would interrupt a round trip that loses nothing.
    if (this.leaveWithoutAsking.consume()) return true;
    return this.confirmDiscardIfWrittenAsync();
  }

  /** True when the page may close: nothing was written, or the user said discard. */
  private async confirmDiscardIfWrittenAsync(): Promise<boolean> {
    if (!this.feedbackDescription.trim()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.feedbackTitle(),
      message: LL.unsavedChanges.discard.feedbackMessage(),
    });
  }

  /**
   * The Sentry id this report is filed under, kept so a retry after a failed send reuses
   * it instead of filing a second one. Undefined until the first attempt resolves it.
   */
  private sentryIdForThisReport: string | undefined;

  /**
   * The Sentry id of the error this report is about, when the page was opened for one
   * (the "noticed something unusual?" toast passes it). Set on every entry: from the
   * page's parameters, or from the screenshot trip when the page is coming back from it.
   */
  private errorIdFromCaller: string | undefined;

  /** Sends the report. Returns true when it actually reached the server. */
  async submitfeedbackReport(): Promise<boolean> {
    let reportSent = false;
    let loader = this.loadingCtrl.create({ content: LL.common.loading.sendingReport() });
    await loader.present();
    try {

      // for the title, extract the first sentence or the first line from the feedback description
      let feedbackTitle = this.feedbackDescription;
      let firstLineIndex = feedbackTitle.indexOf('\n');
      if (firstLineIndex >= 0) {
        feedbackTitle = feedbackTitle.substring(0, firstLineIndex);
      }
      firstLineIndex = feedbackTitle.indexOf('.');
      if (firstLineIndex >= 0) {
        feedbackTitle = feedbackTitle.substring(0, firstLineIndex);
      }
      feedbackTitle = feedbackTitle.trim();
      if (feedbackTitle.length > 60) {
        feedbackTitle = feedbackTitle.substring(0, 60);
        feedbackTitle += '...';
      }

      if (!feedbackTitle) {
        feedbackTitle = LL.sendFeedback.messages.noTitle();
      }

      // Everything in this block belongs to the REPORT, not to the attempt. A failed send
      // now leaves the user on this page to try again, so without the guard each press of
      // Send would file another Sentry message, wait for another flush and count as another
      // `feedback_sent` — many "sent" events for one piece of feedback that may never arrive.
      let sentryId = this.sentryIdForThisReport;
      if (!sentryId) {
        // get sentry id from the caller (in case of an already captured exception or error)
        sentryId = this.errorIdFromCaller;
        // if that was not passed, it means we were not called from an existing sentry error, so capture now
        if (!sentryId) {
          sentryId = Sentry.captureMessage(`Feedback report`, 'info'); // this circumvents the silencer!
        }
        this.sentryIdForThisReport = sentryId;

        // wait for sentry to flush send the message (best-effort — must not block feedback submission)
        try {
          let flushedSuccessfully = await Sentry.flush(2000);
          if (!flushedSuccessfully) {
            console.log('Sentry flush did not complete in time, proceeding without waiting for it');
          }
        } catch (e) {
          console.log('Sentry flush failed, proceeding with feedback submission:', e);
        }

        this.telemetry.trackEvent('feedback_sent', { sentry_id: sentryId, feedback_title: feedbackTitle });
      }
      let imageDataUrl = '';
      // no `&& this.annotator` guard on purpose: the annotator is on screen in exactly
      // this state, so a missing one is a real fault and should travel to the catch
      // below rather than quietly send the report without the picture the user drew on
      if (this.state === FeedbackState.WithScreenshot) {
        imageDataUrl = this.annotator.getAnnotatedImageDataUrl();
      }
      const managingClient = this.userDb.getCoachManagingClient();
      let result = await this.cloudFunctionsService.sendFeedbackAsync({
        feedbackType: this.type,
        feedbackTitle: feedbackTitle,
        feedbackDescription: this.feedbackDescription,
        feedbackScreenshotImgDataUrl: imageDataUrl,
        replyToEmail: this.auth.user.email,
        sentryId: sentryId,
        reporterName: this.auth.user.name,
        commonEventParams: this.telemetry.getCommonEventParams(),
        managedClientUid: managingClient?.clientUid,
        managedClientDisplayName: managingClient?.clientDisplayName,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      reportSent = true;
    } catch (err) {
      console.error(err);
      // Reported at silent-problem tier rather than through `trackAbnormalException`,
      // because the page tells the user about this failure itself, below.
      //
      // `trackAbnormalException` always shows a toast of its own: the user message it
      // is given, or — when it is given none — a generic "we noticed something unusual,
      // report it?" one whose button opens a SECOND, empty feedback page on top of the
      // one still holding this text. Either way we would be talking to the user twice.
      // And it cannot be the single message either, because it returns early, showing
      // nothing at all, for a cloud-function network error and for a retriable 5xx —
      // which is exactly what this callable produces when the send never lands.
      //
      // The `(transient network)` suffix is the convention `session-health.ts` reads to
      // keep an ordinary loss of connectivity from counting as "something went wrong in
      // this session" and silencing the store-review prompt. Any other failure counts,
      // which is what `trackAbnormalException` did here before.
      let neverReachedServer = (err as { name?: string })?.name === 'CloudFunctionNetworkError';
      this.logging.trackSilentProblem(
        neverReachedServer ? 'FeedbackReportSendFailed (transient network)' : 'FeedbackReportSendFailed',
        errorReportFields(err));
    } finally {
      void loader.dismiss();
    }
    if (reportSent) {
      this.toastCtrl.showToast({
        // tell the user that we are sending them email notification about this
        message: LL.sendFeedback.messages.thankYouForFeedback(),
        showCloseButton: true,
        duration: 5000,
      });
    } else {
      this.toastCtrl.showToast({
        // the page stays open behind this, so say the text is still there
        message: LL.sendFeedback.messages.couldNotSend(),
        type: ToastType.WarningStrong,
        showCloseButton: true,
        duration: 8000,
      });
    }
    return reportSent;
  }

}
